import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { validate } from 'uuid';
import {
	Button,
	DialogTitle,
	DialogActions,
	DialogContent,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import useSettings from '../hooks/useSettings';
import useUserData from '../hooks/useUserData';
import { alertLocalizedMsg, executeStored, openLink } from '../Utils';
import ModuleSearchField from '../fields/ModuleSearchField';
import SearchSelectField from '../fields/SearchSelectField';
import Form from '../Form';

interface DeleteDialogProps {
	/** The module name where the delete item is located */
	module: string;
	/** The id of the item to delete */
	id: string | number;
	/** The item title value */
	title?: string;
	/** If true, the deleted item cannot be recovered */
	hardDelete?: boolean;
	/** If true, the item is restored from the recycle bin instead of being deleted */
	restore?: boolean;
	/** Show the replace section, default true  */
	replace?: boolean;
	/** Optional function used to refresh data in views */
	updateRefresh?: () => void;
}

export const showDeleteDialog = (props: DeleteDialogProps) => {
	return openDialog({
		children: <DeleteDialog {...props} />,
		maxWidth: 'xs',
		fullWidth: true,
		onClose: () => {}
	});
};

/**
 * This dialog component allows the user to delete the given item (uid, title properties) or restore it (if restore is true)
 *
 * It can delete permanently if hardDelete is true
 */
export default function DeleteDialog({
	module,
	id,
	title,
	hardDelete,
	restore,
	replace = true,
	updateRefresh
}: DeleteDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(module);
	const userData = useUserData();

	const [replaceId, setReplaceId] = React.useState<any>();
	const [selectedAction, setSelectedAction] = React.useState('null');

	function confirmDelete() {
		let action = `st_${module}_update`;
		let model = {
			[`${settings.modu_short}_uid`]: id,
			[`${settings.modu_short}_user_modified_uid`]: userData.user_uid,
			[`${settings.modu_short}_user_deleted_uid`]: userData.user_uid,
			[`${settings.modu_short}_deleted`]: 1
		};

		if (hardDelete) {
			action = `st_sys_delete_record`;
			model = { id, replace_id: replaceId, module: settings.modu_title };
		}

		if (restore) {
			model[`${settings.modu_short}_user_deleted_uid`] = null;
			model[`${settings.modu_short}_deleted`] = 0;
		}

		executeStored({
			action,
			dispatch,
			data: model,
			successHandler: () => {
				dispatch(alertLocalizedMsg(t, restore ? 'ALERT_RESTORE_SUCCESS' : 'ALERT_DELETE_SUCCESS', 'success'));
				dispatch(closeDialog());
				if (updateRefresh) updateRefresh();
				else if (validate(id) && !hardDelete) openLink({ pathname: '/recycle-bin' });
			}
		});
	}

	return (
		<>
			<DialogTitle>
				{title ? (
					<FormattedMessage
						id="deleteDialog.title"
						defaultMessage={t(restore ? 'DIALOG_TITLE_RESTORE' : 'DIALOG_TITLE_DELETE')}
						values={{ title: title?.replace('"', '') }}
					/>
				) : (
					t('DIALOG_TITLE_DELETE_GENERIC')
				)}
			</DialogTitle>
			{hardDelete && replace && (
				<DialogContent>
					<Form id="deleteDialog">
						<FormControl>
							<RadioGroup
								aria-label="action"
								name="action"
								value={selectedAction}
								onChange={e => setSelectedAction(e.target.value)}
							>
								<FormControlLabel value="null" control={<Radio />} label={t('DIALOG_REPLACE_NULL')} />
								<FormControlLabel value="replace" control={<Radio />} label={t('DIALOG_REPLACE')} />
							</RadioGroup>
						</FormControl>
						{validate(id) ? (
							<ModuleSearchField
								name="replace_id"
								label={t(`MODULE_${module}_NAME`)}
								module={module}
								isEdit={selectedAction === 'replace'}
								sendChange={(_name, value) => setReplaceId(value)}
							/>
						) : (
							<SearchSelectField
								name="replace_id"
								label={t(`MODULE_${module}_NAME`)}
								systable={module}
								filters={[
									{
										filt_field: `${settings.modu_short}_id`,
										filt_clause: 'not_equal',
										filt_value: id
									}
								]}
								isEdit={selectedAction === 'replace'}
								sendChange={(_name, value) => setReplaceId(value)}
							/>
						)}
					</Form>
				</DialogContent>
			)}
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button onClick={confirmDelete}>{t('DIALOG_ACTION_CONFIRM')}</Button>
			</DialogActions>
		</>
	);
}
